import { CardsDataSourceBase } from '../../common/CardsDataSourceBase';
import { injectable } from 'inversify';
import { injectToken } from 'inversify-token';
import { YinzCamAPIResponse } from 'yinzcam-api';
import { YinzCamAppServerToken } from 'yinzcam-appserver';

// Pulls indexed Stat1..StatN values out of a StatsGroup's _attributes, skipping
// Stat0 (the name/label column). With `count` provided, emits exactly `count`
// items padded with ''; otherwise runs to the first missing StatN.
function extractStats(attrs: Record<string, string> | undefined, count?: number): string[] {
  const vals: string[] = [];
  if (count !== undefined) {
    for (let i = 1; i <= count; i++) vals.push(String(attrs?.[`Stat${i}`] ?? ''));
    return vals;
  }
  for (let i = 1; ; i++) {
    const val = attrs?.[`Stat${i}`];
    if (val === undefined || val === null) break;
    vals.push(String(val));
  }
  return vals;
}

@injectable()
export class YinzCamAppServerGameScorecard extends CardsDataSourceBase {
  public constructor(@injectToken(YinzCamAppServerToken) server) {
    super({ server });
  }

  protected processResponse(
    path: string,
    response: YinzCamAPIResponse,
    specData?: { [key: string]: any }
  ): object | any[] {
    if ((response as any)?.isStatusNot2xx) {
      throw new Error(`YinzCamAppServerGameScorecard: upstream returned ${(response as any)?.status} for ${path}`);
    }
    const data = response?.data as any;
    const rawInnings = [].concat(data?.Scorecard?.Innings?.Inning).filter(Boolean);

    const innings = rawInnings.map((inning: any) => {
      const title: string = inning?._attributes?.Title ?? '';
      const sections = [].concat(inning?.StatsSection).filter(Boolean);

      const battingSec = sections.find((s: any) => s?._attributes?.Type === 'Batting');
      const summarySec = sections.find((s: any) => s?._attributes?.Type === 'BattingSummary');
      const bowlingSec = sections.find((s: any) => s?._attributes?.Type === 'Bowling');
      const fowSec = sections.find((s: any) => s?._attributes?.Type === 'FallOfWickets');

      const battingColumns = extractStats(battingSec?.StatsGroup?._attributes);
      const battingStats = []
        .concat(battingSec?.PlayerStats)
        .filter(Boolean)
        .map((ps: any) => ({
          playerName: ps?.StatsGroup?._attributes?.Stat0 ?? '',
          handedness: ps?._attributes?.Handedness ?? '',
          dismissal: ps?._attributes?.HowOut ?? '',
          isBold: ps?._attributes?.IsBold === 'true',
          values: extractStats(ps?.StatsGroup?._attributes, battingColumns.length),
        }));

      const summaryRows = [].concat(summarySec?.PlayerStats).filter(Boolean);
      const extrasAttrs = summaryRows[0]?.StatsGroup?._attributes;
      const totalAttrs = summaryRows[1]?.StatsGroup?._attributes;

      const bowlingColumns = extractStats(bowlingSec?.StatsGroup?._attributes);
      const bowlingStats = []
        .concat(bowlingSec?.PlayerStats)
        .filter(Boolean)
        .map((ps: any) => ({
          playerName: ps?.StatsGroup?._attributes?.Stat0 ?? '',
          values: extractStats(ps?.StatsGroup?._attributes, bowlingColumns.length),
        }));

      const fowRows = [].concat(fowSec?.PlayerStats).filter(Boolean);
      const fowParts = fowRows.map((ps: any) => {
        const a = ps?.StatsGroup?._attributes;
        return `${a?.Stat1} (${a?.Stat0}, ${a?.Stat2} ov)`;
      });
      const fallOfWicketsText = fowParts.length ? `Fall of Wickets: ${fowParts.join(', ')}` : '';

      // Innings titles arrive as "1st Innings — Nottinghamshire"; team name is
      // after the em/en/hyphen dash.
      const titleParts = title.split(/\s*[—–-]\s*/);
      const battingTeamName = titleParts[1] || titleParts[0] || '';

      return {
        label: title,
        battingTeamName,
        battingColumns,
        battingStats,
        extrasLabel: extrasAttrs?.Stat0 ?? 'Extras',
        extrasValue: extrasAttrs?.Stat1 ?? '',
        extrasDetail: extrasAttrs?.Stat2 ? `(${extrasAttrs.Stat2})` : '',
        totalLabel: totalAttrs?.Stat0 ?? 'Total',
        totalValue: totalAttrs?.Stat1 ?? '',
        totalDetail: totalAttrs?.Stat2 ? `(${totalAttrs.Stat2})` : '',
        fallOfWicketsText,
        bowlingTeamName: '',
        bowlingColumns,
        bowlingStats,
      };
    });

    return { innings };
  }
}
